import { ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, Tooltip, Typography, TypographyProps, useMediaQuery, useTheme } from '@mui/material';

export enum VERIFICATION_STATUS {
  VERIFIED = 'VERIFIED',
  FAILED = 'FAILED',
  INCOMPLETE = 'INCOMPLETE',
}

interface VerificationIndicatorProps {
  prefix?: ReactNode;
  status: VERIFICATION_STATUS;
  text?: ReactNode;
  disableText?: boolean;
  sx?: TypographyProps['sx'];
}

const VerificationStatusMap: Record<
  VERIFICATION_STATUS,
  { icon: ReactNode; color: TypographyProps['color']; text: ReactNode }
> = {
  [VERIFICATION_STATUS.VERIFIED]: {
    icon: <CheckCircleIcon />,
    color: (theme) => theme.palette.success.main,
    text: <FormattedMessage id="profile.account.verification.status.verified" defaultMessage="Verified" />,
  },
  [VERIFICATION_STATUS.FAILED]: {
    icon: <ErrorIcon />,
    color: (theme) => theme.palette.danger,
    text: <FormattedMessage id="profile.account.verification.status.error" defaultMessage="Issue with verification" />,
  },
  [VERIFICATION_STATUS.INCOMPLETE]: {
    icon: <ErrorIcon />,
    color: (theme) => theme.palette.danger,
    text: <FormattedMessage id="profile.account.verification.status.incomplete" defaultMessage="Needs Verification" />,
  },
};

export const VerificationIndicator = ({ prefix, status, disableText, text, sx }: VerificationIndicatorProps) => {
  const theme = useTheme();
  const isTooltipEnabled = useMediaQuery(theme.breakpoints.down('md'));

  const passedText = useMemo(
    () => (!disableText ? <>{text ?? VerificationStatusMap[status].text}</> : undefined),
    [disableText, text, status]
  );

  const body = (
    <Typography
      variant="body1.medium"
      component="span"
      display="flex"
      alignItems="center"
      gap={0.5}
      color={VerificationStatusMap[status].color}
      sx={sx}
    >
      {VerificationStatusMap[status].icon}

      <Box display={{ xs: 'none', md: 'block' }}>
        {prefix} {passedText}
      </Box>
    </Typography>
  );

  if (isTooltipEnabled) {
    return <Tooltip title={passedText}>{body}</Tooltip>;
  }

  return body;
};
